export const enPipedrive = {
	title: "Pipedrive CRM Integration",
	connected: "Connected",
	notConnected: "Not Connected",
	connect: "Connect",
	disconnect: "Disconnect",
	configureFieldMapping: "Configure Field Mapping",
	fieldMapping: {
		title: "Pipedrive Field Mapping",
		leads: "Leads",
		persons: "Persons",
		organizations: "Organizations",
		deals: "Deals",
		ourField: "Signalist Field",
		pipedriveField: "Pipedrive Field",
		active: "Active",
		actions: "Actions",
		addMapping: "Add Mapping",
		addMissingDefaults: "Add missing defaults",
		addMissingDefaultsTooltip:
			"Adds the default mappings this tab is missing: the standard fields, and your own custom fields whose name says what they hold (for example Job title, LinkedIn or Website). Existing mappings are never changed.",
		addMissingDefaultsNoneAdded: "All default mappings are already configured.",
		addMissingDefaultsFailed: "The default mappings could not be added.",
		saveAll: "Save All",
		selectField: "Select a field",
		noMappings: "No field mappings configured yet.",
		deleteConfirm: "Are you sure you want to delete this mapping?",
		sourceTypeFromRecord: "From record",
		sourceTypeConstant: "Constant value",
		sourceTypeAi: "AI-generated",
		constantPlaceholder: "Enter a literal value",
		constantLabel: 'Constant: "{{value}}"',
		aiBadge: "AI",
		aiConfigure: "Configure",
		aiTargetPlaceholder: "Pick a Pipedrive field →",
		aiCreditTooltip: "0.1 credits per lead synced — flat, covers all AI fields",
		aiHintTitle: "AI-enriched fields",
		aiHintBody:
			"Let an AI agent classify, research, or extract a value before syncing. AI enrichment costs 0.1 credits per lead — flat, regardless of how many AI fields you add. The field name will match your selected Pipedrive field.",
		aiSaveDisabledTooltip: "Configure AI to save",
		cannotMap: "— Cannot map —",
		cannotMapTooltip: "Pipedrive reserved field — set in deal configuration instead",
		leadCannotMapTooltip:
			"Pipedrive does not accept this field on a lead — the sync skips this mapping",
		leadTargetNoResults:
			"A Pipedrive lead only takes custom fields. Email, name and website are mapped on the Persons and Organizations tabs.",
		metaBadge: {
			required: "Required",
			multi: "Multi",
			dropdown: "Dropdown",
			number: "Number",
			calculated: "Calculated",
			readOnly: "Read-only",
		},
		footerSummary: "{{mapped}} fields mapped · {{ai}} AI-enriched",
		footerCreditPill: "+0.1 credits per lead with AI enrichment",
		aiConfigTitle: "Configure AI-enriched field",
		aiConfigSubtitle: "Target: {{label}}",
		aiConfig: {
			configurationTitle: "Configuration",
			promptLabel: "Prompt",
			promptPlaceholder: "Tell the AI how to generate this CRM field value...",
			promptHelp:
				"The AI runs once per sync with this prompt and the prospect and organization data as input.",
			promptMeta: "Editable · pre-filled from field metadata",
			defaultPrompt:
				"Generate a value for {{target}}. Use the available prospect and organization context. Allowed values: {{allowedValues}}. Return only the final value.",
			insertVariable: "Insert variable",
			variablesLabel: "Variables",
			allowedValuesTitle: "Allowed values",
			allowedValuesEmpty: "No allowed values returned for this field.",
			fallbackLabel: "Fallback value",
			fallbackHelp:
				"Used when the AI cannot determine a confident value. For dropdowns, the fallback must match one of the allowed values.",
			leaveEmpty: "Leave empty",
			behaviorTitle: "Behavior",
			syncReasoningLabel: "Sync reasoning",
			syncReasoningHelp: "Write the AI reasoning to another text field.",
			syncReasoningTargetLabel: "Sync reasoning to",
			syncReasoningTargetHelp: "Only text and textarea fields can receive reasoning.",
			syncSourceLabel: "Sync source",
			syncSourceHelp: "Write the source list to another text field.",
			syncSourceTargetLabel: "Sync source to",
			syncSourceTargetHelp: "Only text and textarea fields can receive sources.",
			secondaryTargetPlaceholder: "Select a text field",
			testTitle: "Test",
			freeDuringSetup: "Free during setup",
			testIntroTitle: "Test this prompt with a prospect from your pipeline",
			testIntroHelp:
				"Load the variables for a prospect, adjust values if needed, and run a free test.",
			prospectLabel: "Prospect",
			prospectPlaceholder: "Search prospects...",
			loadVariables: "Load variables",
			variablesPreviewTitle: "Variables preview",
			noVariables: "Pick a prospect and load variables to preview the prompt data.",
			editData: "Edit data",
			revertEdits: "Revert edits",
			runTest: "Run test",
			rerunTest: "Re-run test",
			testRunHelp: "Test runs are free, do not update the CRM, and do not consume credits.",
			progressLabel: "Load lead data → analyze & match → return result",
			loadingLeadData: "Load prospect data",
			loadingResearch: "Research organization",
			loadingAnalyze: "Analyze & match",
			loadingReturn: "Return result",
			resultTitle: "Result",
			resultReadyTitle: "AI returned a value",
			valueLabel: "Value",
			reasoningLabel: "Reasoning",
			sourcesLabel: "Sources",
			durationLabel: "Duration",
			fallbackUsed: "Fallback was used.",
			finalPrompt: "Final prompt",
			emptyState: "No test run yet.",
			noCredits: "Tests do not consume credits and do not write to the CRM.",
			costReminder: "0.1 credits per lead synced (flat — covers all AI fields)",
			cancel: "Cancel",
			save: "Save AI mapping",
			requiredPrompt: "Prompt is required",
			requiredSecondaryField: "Select a text field",
			invalidFallback: "Fallback must be one of the allowed values",
			testError: "AI test failed. Please try again.",
		},
	},
	tabs: {
		connection: "Connection",
		settings: "Settings",
		dealConfiguration: "Deal Configuration",
		leadConfiguration: "Lead Configuration",
		userMapping: "User Mapping",
		syncLogs: "Sync Logs",
	},
	syncTrigger: {
		title: "Sync Prospects to Pipedrive CRM",
		description: "Choose when prospects should be automatically synchronized to Pipedrive.",
		DISABLED: "Disabled",
		ON_CAMPAIGN_START: "When campaign starts",
		ON_REPLY: "When prospect replies",
		INHERIT: "Use company default",
		inheritedFrom: "Currently using company setting",
	},
	dealCreationTrigger: {
		title: "Automatic Deal Creation",
		description:
			"Choose when deals or leads should be automatically created in Pipedrive for prospects that already exist.",
		DISABLED: "Off",
		ON_RESPONSE: "On Response",
		ON_POSITIVE_RESPONSE: "On Positive Response",
		createAsLead: "Create as Lead instead of Deal",
		createAsLeadHelp:
			"When enabled, automatic creation produces a Lead in the Pipedrive Leads inbox instead of a full Deal.",
	},
	dealConfig: {
		title: "Deal Defaults",
		description: "Configure default values for new Pipedrive deals.",
		defaultPipeline: "Default Pipeline",
		defaultStage: "Default Stage",
		dealNameTemplate: "Deal Name Template",
		dealNameTemplateHelp:
			"Use {firstName}, {lastName}, {company}, {email}, {title} as placeholders.",
		insertPlaceholder: "Insert placeholder",
		closeDateOffsetDays: "Expected Close Date Offset (days)",
		closeDateOffsetHelp: "Number of days from today for the default expected close date (1-365).",
		defaultOwner: "Default Deal Owner",
		defaultCurrency: "Default Currency",
		selectPipeline: "Select a pipeline",
		selectStage: "Select a stage",
		selectOwner: "Select an owner",
		saved: "Deal configuration saved successfully.",
		saveError: "Failed to save deal configuration.",
	},
	leadConfig: {
		title: "Lead Defaults",
		description:
			"Configure default values applied to new Pipedrive Leads. Applies when the deal-creation trigger has 'Create as Lead' enabled, and when manually creating a Lead from a prospect.",
		defaultCurrency: "Default Currency",
		defaultValue: "Default Value",
		defaultValueHelp:
			"Monetary value assigned to the Lead. Skipped when a value is already produced by a LEAD field mapping.",
		defaultOwner: "Default Lead Owner",
		closeDateOffsetDays: "Expected Close Date Offset (days)",
		closeDateOffsetHelp: "Number of days from today for the Lead's expected close date (1-365).",
		defaultLabelIds: "Default Label IDs",
		defaultLabelIdsHelp:
			"Comma-separated Pipedrive Lead label IDs (max 20). Skipped when label_ids is already produced by a LEAD field mapping.",
		defaultLabelIdsCount: "{{count}} label ID(s)",
		saved: "Lead configuration saved successfully.",
		saveError: "Failed to save lead configuration.",
	},
	userMapping: {
		title: "User Mapping",
		description: "Map Signalist users to Pipedrive owners for deal assignment.",
		signalistUser: "Signalist User",
		pipedriveOwner: "Pipedrive Owner",
		selectOwner: "Select an owner",
		noUsers: "No sending users found.",
		saved: "User mappings saved successfully.",
		saveError: "Failed to save user mappings.",
	},
	createDeal: {
		title: "Create Pipedrive Deal",
		titleDeal: "Create Pipedrive Deal",
		titleLead: "Create Pipedrive Lead",
		submitDeal: "Create Deal",
		submitLead: "Create Lead",
		createAsLead: "Create as Lead",
		createAsLeadHelp: "Leads live in the Pipedrive Leads inbox before becoming deals.",
		dealName: "Deal Name",
		leadTitle: "Lead Title",
		pipeline: "Pipeline",
		stage: "Deal Stage",
		owner: "Owner",
		closeDate: "Expected Close Date",
		selectPipeline: "Select a pipeline",
		selectStage: "Select a stage",
		selectOwner: "Select an owner",
		success: "Created in Pipedrive.",
		error: "Failed to create record in Pipedrive.",
		missingConfig: "Please configure deal defaults in Pipedrive settings first.",
	},
	conversationSync: {
		title: "Conversation Sync",
		label: "Sync conversations to Pipedrive",
		description:
			"When enabled, messages sent to and received from prospects are automatically synced as notes on the matching Pipedrive record.",
		success: "Conversation sync setting updated successfully.",
		error: "Failed to update conversation sync setting.",
	},
	oauth: {
		connectWithPipedrive: "Connect with Pipedrive",
		connectedViaOAuth: "Connected via OAuth",
		companyDomain: "Company Domain",
		pipedriveUserId: "Pipedrive User ID",
		tokenRefresh: "Token refreshes automatically",
		disconnectTitle: "Disconnect Pipedrive?",
		disconnectBody: "This will remove the OAuth connection. Pipedrive syncing will stop.",
		connectError: "Failed to generate authorization URL",
		alreadyDisconnected: "Pipedrive is already disconnected.",
		disconnectError: "Failed to disconnect from Pipedrive.",
		// PREC-4353: the stored sign-in was refused; the flag clears on the next sign-in.
		reconnectRequired: "Reconnect required",
		reauthDescription:
			"Pipedrive no longer accepts the stored sign-in, so nothing syncs. Reconnect to sign in again; your settings and field mappings are kept.",
		reconnect: "Reconnect",
	},
	syncLogs: {
		description: "View the history of data synchronization between Signalist and Pipedrive.",
		filterByEntity: "Filter by Entity",
		allEntities: "All Entities",
		entityType: "Entity Type",
		status: "Status",
		synced: "Synced",
		failed: "Failed",
		error: "Error",
		startedAt: "Started At",
		completedAt: "Completed At",
		noLogs: "No sync logs found.",
	},
	resync: {
		title: "Resync with Pipedrive",
		description:
			"Re-push every already-synced prospect to Pipedrive using your current field mapping. Useful after you add or change mapped fields.",
		button: "Resync now",
		confirmTitle: "Resync with Pipedrive?",
		confirmBody:
			"This will re-push every prospect that is already synced to Pipedrive using your current field mapping. Continue?",
		confirmCta: "Resync",
		success: "Re-syncing {{count}} contact(s) to Pipedrive. Check the sync log for progress.",
		empty: "No already-synced prospects found.",
		error: "Failed to queue resync. Please try again.",
	},
};
